import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { useToast } from '@/components/ui/use-toast';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import StickyCta from '@/components/layout/StickyCta';
import CookieConsentBanner from '@/components/layout/CookieConsentBanner';
import LoadingScreen from '@/components/layout/LoadingScreen'; 
import HomePage from '@/pages/HomePage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import CookiePolicyPage from '@/pages/CookiePolicyPage';
import AboutPage from '@/pages/AboutPage';
import ChangelogPage from '@/pages/ChangelogPage';
import SecurityPage from '@/pages/SecurityPage';
import BlogPage from '@/pages/BlogPage';
import BlogPostPage from '@/pages/BlogPostPage';
import CareersPage from '@/pages/CareersPage';
import ContactPage from '@/pages/ContactPage';
import NotFoundPage from '@/pages/NotFoundPage';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import AdminRoute from '@/components/auth/AdminRoute';
import { AuthProvider, useAuth } from '@/contexts/SupabaseAuthContext';

const JobDetailPage = lazy(() => import('@/pages/JobDetailPage.jsx'));
const UseCasesOverviewPage = lazy(() => import('@/pages/use-cases/UseCasesOverviewPage.jsx'));
const UseCaseDetailPage = lazy(() => import('@/pages/use-cases/UseCaseDetailPage.jsx'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage.jsx'));
const SignupPage = lazy(() => import('@/pages/auth/SignupPage.jsx'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage.jsx'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage.jsx'));
const AdminDashboardLayout = lazy(() => import('@/pages/admin/AdminDashboardLayout.jsx'));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage.jsx'));
const AdminPlansPage = lazy(() => import('@/pages/admin/AdminPlansPage.jsx'));
const AdminRoleManagerPage = lazy(() => import('@/pages/admin/AdminRoleManagerPage.jsx'));


const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (pathname.startsWith('/solutions/')) {
      return; 
    }
    if (!hash) {
      window.scrollTo(0, 0);
    } else {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        const navbarHeight = document.querySelector('nav')?.offsetHeight || 0;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY - navbarHeight - 20; 
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth',
        });
      }
    }
  }, [pathname, hash]);
  return null;
};

const AppContent = () => {
  const { toast } = useToast();
  const { loading, user, profile } = useAuth();
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    analytics: false,
    preferences: false,
    marketing: false,
  });

  const showComingSoonToast = (feature) => {
    toast({
      title: "🚧 Feature Coming Soon!",
      description: `${feature || 'This feature'} isn't implemented yet—but we're working on it! 🚀`,
      duration: 3000,
      className: 'bg-card text-card-foreground border-border',
    });
  };

  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.documentElement.setAttribute('data-theme', 'dark');
    
    const savedConsent = localStorage.getItem('webfiny_cookie_consent');
    const savedPreferences = localStorage.getItem('webfiny_cookie_preferences');

    if (!savedConsent) {
      setShowCookieBanner(true);
    } else {
      setShowCookieBanner(false); 
    }

    if (savedPreferences) {
      setCookiePreferences(JSON.parse(savedPreferences));
    }

    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowStickyCta(true);
      } else {
        setShowStickyCta(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.documentElement.classList.remove('dark');
      document.documentElement.removeAttribute('data-theme');
    };
  }, []);

  const handleAcceptAllCookies = () => {
    const allAccepted = { necessary: true, analytics: true, preferences: true, marketing: true };
    localStorage.setItem('webfiny_cookie_consent', 'all');
    localStorage.setItem('webfiny_cookie_preferences', JSON.stringify(allAccepted));
    setCookiePreferences(allAccepted);
    setShowCookieBanner(false);
     toast({ title: "Cookies Accepted", description: "You've accepted all cookies.", className: 'bg-green-600 text-white border-green-700' });
  };

  const handleRejectAllCookies = () => {
    const essentialOnly = { necessary: true, analytics: false, preferences: false, marketing: false };
    localStorage.setItem('webfiny_cookie_consent', 'essential');
    localStorage.setItem('webfiny_cookie_preferences', JSON.stringify(essentialOnly));
    setCookiePreferences(essentialOnly);
    setShowCookieBanner(false);
    toast({ title: "Cookies Rejected", description: "You've opted for essential cookies only.", className: 'bg-destructive text-destructive-foreground border-destructive' });
  };
  
  const handleSavePreferences = (newPreferences) => {
    localStorage.setItem('webfiny_cookie_consent', 'custom');
    localStorage.setItem('webfiny_cookie_preferences', JSON.stringify(newPreferences));
    setCookiePreferences(newPreferences);
    setShowCookieBanner(false);
    toast({ title: "Preferences Saved", description: "Your cookie preferences have been saved.", className: 'bg-blue-600 text-white border-blue-700' });
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-body flex flex-col">
      <Navbar onActionClick={showComingSoonToast} />
      <main className="flex-grow pt-16"> 
        <ScrollToTop />
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={user ? (profile?.role === 'Admin' ? <Navigate to="/admin" replace /> : <Navigate to="/dashboard" replace />) : <HomePage onActionClick={showComingSoonToast} />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/blog" element={<BlogPage onActionClick={showComingSoonToast} />} />
            <Route path="/blog/:slug" element={<BlogPostPage onActionClick={showComingSoonToast} />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/careers/:jobId" element={<JobDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />

            <Route path="/solutions" element={<UseCasesOverviewPage />} />
            <Route path="/solutions/:slug" element={<UseCaseDetailPage />} />
            
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            
            <Route path="/admin" element={<AdminRoute><AdminDashboardLayout /></AdminRoute>}>
              <Route index element={<Navigate to="/admin/users" replace />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="roles" element={<AdminRoleManagerPage />} />
              <Route path="plans" element={<AdminPlansPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer onActionClick={showComingSoonToast} />
      <StickyCta isVisible={showStickyCta} onActionClick={() => window.open('https://crafter.webfiny.com/', '_blank', 'noopener,noreferrer')} />
      {showCookieBanner && (
        <CookieConsentBanner 
          onAcceptAll={handleAcceptAllCookies}
          onRejectAll={handleRejectAllCookies}
          onSavePreferences={handleSavePreferences}
          initialPreferences={cookiePreferences}
        />
      )}
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;